import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, Search, Eye, ImageOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../contexts/ToastContext';
import {
  listarValidacionesKyc, obtenerValidacionKyc,
  type FiltrosValidaciones, type ValidacionResumen, type ValidacionDetalle, type DecisionKyc,
} from '../../../services/kycService';
import { mensajeError } from '../correo/errores';
import { fmtFecha } from '../correo/formato';
import { DecisionChip } from './ui';
import { DECISIONES, DECISION_ETIQUETA, etiquetaPlataforma, fmtPuntaje } from './formato';
import styles from './KycModule.module.css';

const TAMANO = 25;

/**
 * Lista de validaciones paginada en el servidor: cada página trae 25 registros y un
 * cursor para la siguiente. Nunca se cargan todos (la tabla tiene decenas de miles).
 * «Anterior» reutiliza los cursores ya recibidos.
 */
export default function KycValidacionesPage() {
  const { addToast } = useToast();
  const [filtros, setFiltros] = useState<FiltrosValidaciones>({ decision: '' });
  const [aplicados, setAplicados] = useState<FiltrosValidaciones>({ decision: '' });
  // cursores[i] = cursor con el que se pide la página i (la 0 empieza sin cursor).
  const [cursores, setCursores] = useState<(string | null)[]>([null]);
  const [pagina, setPagina] = useState(0);
  const [items, setItems] = useState<ValidacionResumen[]>([]);
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<ValidacionDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const reqId = useRef(0);
  const cargar = useCallback(async (f: FiltrosValidaciones, cursor: string | null, indice: number, pila: (string | null)[]) => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const r = await listarValidacionesKyc({ ...f, cursor, tamano: TAMANO });
      if (id !== reqId.current) return;
      setItems(r.items);
      setSiguiente(r.cursor);
      setPagina(indice);
      const nueva = pila.slice(0, indice + 1);
      if (r.cursor) nueva[indice + 1] = r.cursor;
      setCursores(nueva);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [addToast]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(aplicados, null, 0, [null]); }, []);

  const buscar = () => {
    const f = { ...filtros, cedula: filtros.cedula?.trim() || undefined };
    setAplicados(f);
    cargar(f, null, 0, [null]);
  };

  const detalleReq = useRef(0);
  const verDetalle = async (validationId: string) => {
    const id = ++detalleReq.current;
    setDetalle(null);
    setCargandoDetalle(true);
    try {
      const d = await obtenerValidacionKyc(validationId);
      if (id === detalleReq.current) setDetalle(d);
    } catch (err) {
      if (id === detalleReq.current) { addToast('error', mensajeError(err)); setCargandoDetalle(false); }
    } finally {
      if (id === detalleReq.current) setCargandoDetalle(false);
    }
  };
  const cerrarDetalle = () => {
    detalleReq.current++;
    setDetalle(null);
    setCargandoDetalle(false);
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.campo}>
          <label htmlFor="kyc-cedula">Cédula</label>
          <input id="kyc-cedula" type="text" placeholder="8-123-456" value={filtros.cedula ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, cedula: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="kyc-decision">Resultado</label>
          <select id="kyc-decision" value={filtros.decision ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, decision: e.target.value as DecisionKyc | '' }))}>
            <option value="">Todos</option>
            {DECISIONES.map((d) => <option key={d} value={d}>{DECISION_ETIQUETA[d]}</option>)}
          </select>
        </div>
        <div className={styles.campo}>
          <label htmlFor="kyc-v-desde">Desde</label>
          <input id="kyc-v-desde" type="date" value={filtros.desde ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value || undefined }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="kyc-v-hasta">Hasta</label>
          <input id="kyc-v-hasta" type="date" value={filtros.hasta ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value || undefined }))} />
        </div>
        <Button variant="secondary" size="sm" onClick={buscar} disabled={cargando}>
          <Search size={15} /> Buscar
        </Button>
      </div>

      {cargando && items.length === 0 ? (
        <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>
      ) : items.length === 0 ? (
        <div className={styles.vacio}>No hay validaciones para el filtro seleccionado.</div>
      ) : (
        <>
          <div className={styles.tablaScroll} aria-busy={cargando}>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th>Fecha</th><th>Nombre</th><th>Cédula</th><th>Resultado</th>
                  <th>Liveness</th><th>Coincidencia</th><th>Plataforma</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.validationId}>
                    <td>{fmtFecha(v.createdAt)}</td>
                    <td>{v.nombre || '—'}</td>
                    <td>{v.cedula && v.cedula !== 'SIN-CEDULA' ? v.cedula : '—'}</td>
                    <td><DecisionChip decision={v.decision} /></td>
                    <td>{fmtPuntaje(v.livenessConfidence)}</td>
                    <td>{fmtPuntaje(v.faceMatchScore)}</td>
                    <td>{etiquetaPlataforma(v.platform)}{v.appVersion ? ` · ${v.appVersion}` : ''}</td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => verDetalle(v.validationId)}>
                        <Eye size={15} /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.acciones} style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
            {cargando && <Loader2 className="spinner" size={16} />}
            <span className={styles.kpiEtiqueta}>Página {pagina + 1} · {TAMANO} por página</span>
            <Button variant="ghost" size="sm" disabled={cargando || pagina === 0}
              onClick={() => cargar(aplicados, cursores[pagina - 1], pagina - 1, cursores)}>Anterior</Button>
            <Button variant="ghost" size="sm" disabled={cargando || !siguiente}
              onClick={() => cargar(aplicados, siguiente, pagina + 1, cursores)}>Siguiente</Button>
          </div>
        </>
      )}

      <Modal open={detalle !== null || cargandoDetalle} onClose={cerrarDetalle} title="Detalle de la validación" size="lg">
        {cargandoDetalle || !detalle ? (
          <div className={styles.vacio}><Loader2 className="spinner" size={24} /></div>
        ) : (
          <DetalleValidacion v={detalle} />
        )}
      </Modal>
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }) {
  return (
    <div className={styles.dato}>
      <span className={styles.datoEtiqueta}>{etiqueta}</span>
      <span className={styles.datoValor}>{valor || '—'}</span>
    </div>
  );
}

function Puntaje({ etiqueta, valor, color }: { etiqueta: string; valor: number; color: string }) {
  const ancho = Math.max(0, Math.min(100, valor));
  return (
    <div className={styles.puntaje}>
      <span>{etiqueta}</span>
      <span className={styles.puntajeBarra}><span style={{ width: `${ancho}%`, background: color }} /></span>
      <span className={styles.puntajeValor}>{fmtPuntaje(valor)}</span>
    </div>
  );
}

function Imagen({ titulo, url, borradas }: { titulo: string; url: string | null; borradas: boolean }) {
  return (
    <figure className={styles.imagen}>
      <figcaption className={styles.datoEtiqueta}>{titulo}</figcaption>
      {url ? (
        <img src={url} alt={titulo} loading="lazy" />
      ) : (
        <div className={styles.imagenVacia}>
          <span><ImageOff size={22} aria-hidden /><br />
            {borradas ? 'Imagen eliminada por la política de retención.' : 'No hay imagen registrada.'}</span>
        </div>
      )}
    </figure>
  );
}

function DetalleValidacion({ v }: { v: ValidacionDetalle }) {
  const borradas = !!v.imagesDeletedAt;
  return (
    <div>
      <div className={styles.acciones} style={{ marginBottom: '1rem' }}>
        <DecisionChip decision={v.decision} />
        <span className={styles.kpiEtiqueta}>{fmtFecha(v.createdAt)}</span>
      </div>
      {v.errorMessage && <p className={styles.error}>{v.errorMessage}</p>}

      <div className={styles.seccionTitulo}>Persona</div>
      <div className={styles.detalleGrid}>
        <Dato etiqueta="Nombre" valor={[v.firstName, v.lastName].filter(Boolean).join(' ')} />
        <Dato etiqueta="Cédula" valor={v.cedula !== 'SIN-CEDULA' ? v.cedula : ''} />
        <Dato etiqueta="Fecha de nacimiento" valor={v.dateOfBirth} />
        <Dato etiqueta="Documento" valor={v.documentType} />
        <Dato etiqueta="País" valor={v.country} />
      </div>

      <div className={styles.seccionTitulo}>Puntajes</div>
      <div className={styles.puntajes}>
        <Puntaje etiqueta="Liveness" valor={v.livenessConfidence} color="#8b5cf6" />
        <Puntaje etiqueta="Coincidencia" valor={v.faceMatchScore} color="#14b8a6" />
        <Puntaje etiqueta="General" valor={v.overallConfidence} color="#3b82f6" />
      </div>

      <div className={styles.seccionTitulo}>Imágenes</div>
      <div className={styles.imagenes}>
        <Imagen titulo="Documento" url={v.documentoUrl} borradas={borradas} />
        <Imagen titulo="Selfie" url={v.selfieUrl} borradas={borradas} />
      </div>
      {!borradas && (v.documentoUrl || v.selfieUrl) && (
        <p className={styles.aviso}>
          Los enlaces de las imágenes vencen en {Math.round(v.imagenesVigenciaSegundos / 60)} minutos; vuelva a abrir el detalle para renovarlos.
          La consulta queda registrada en la auditoría.
        </p>
      )}
      {borradas && (
        <p className={styles.aviso}>Imágenes eliminadas el {fmtFecha(v.imagesDeletedAt)}{v.imagesDeletedReason ? ` (${v.imagesDeletedReason})` : ''}.</p>
      )}

      <div className={styles.seccionTitulo} style={{ marginTop: '1.25rem' }}>Técnico</div>
      <div className={styles.detalleGrid}>
        <Dato etiqueta="Plataforma" valor={`${etiquetaPlataforma(v.platform)}${v.appVersion ? ` · ${v.appVersion}` : ''}`} />
        <Dato etiqueta="Modo de liveness" valor={v.livenessMode} />
        <Dato etiqueta="Códigos de motivo" valor={v.reasonCodes.join(', ')} />
        <Dato etiqueta="ID de validación" valor={v.validationId} />
        <Dato etiqueta="ID de sesión" valor={v.sessionId} />
      </div>
    </div>
  );
}
